// slices/chat/chatSelectors.ts
import { createSelector } from '@reduxjs/toolkit';
import type {
  ChatActionProposal,
  ChatBlock,
  ChatConversation,
  ChatMessage,
  ChatState,
  ChatTurn,
} from './chatSlice';
import type { ChatDailyLimits, ChatFollowup } from '../../types/chatStream';

/**
 * Selectors take the chat state, not the root state, so the drawer and the page
 * (and tests, which build a bare chat store) read through exactly the same
 * functions. `selectChatState` is the only place that knows the mount point.
 */
export interface ChatRootState {
  chat: ChatState;
}

export const selectChatState = (state: ChatRootState): ChatState => state.chat;

/**
 * Shared empty results. A selector that builds `[]` on the miss path hands
 * `useSelector` a new reference on every render, which React Redux flags and
 * which re-renders the thread for nothing.
 */
const NO_MESSAGE_IDS: string[] = [];
const NO_FOLLOWUPS: ChatFollowup[] = [];

export const selectActiveConversationId = (state: ChatRootState) =>
  state.chat.activeConversationId;

export const selectConversation = (
  state: ChatRootState,
  conversationId: string | null
): ChatConversation | null =>
  conversationId ? (state.chat.conversations[conversationId] ?? null) : null;

export const selectConversationMessageIds = (
  state: ChatRootState,
  conversationId: string | null
): string[] =>
  selectConversation(state, conversationId)?.messageIds ?? NO_MESSAGE_IDS;

export const selectMessage = (
  state: ChatRootState,
  messageId: string
): ChatMessage | null => state.chat.messages[messageId] ?? null;

export const selectTurn = (
  state: ChatRootState,
  turnId: string | null
): ChatTurn | null => (turnId ? (state.chat.turns[turnId] ?? null) : null);

export const selectActiveTurn = (
  state: ChatRootState,
  conversationId: string | null
): ChatTurn | null =>
  selectTurn(
    state,
    selectConversation(state, conversationId)?.activeTurnId ?? null
  );

/**
 * The user's daily chat allowance. Not conversation-scoped: the cap is per
 * user, so the composer stays locked at zero whichever thread is open.
 */
export const selectChatLimits = (
  state: ChatRootState
): ChatDailyLimits | null => state.chat.limits;

/** True once the server says the user has no chats left today. */
export const selectIsChatLimitReached = (state: ChatRootState): boolean => {
  const remaining = state.chat.limits?.chatsRemaining;
  return remaining !== null && remaining !== undefined && remaining <= 0;
};

/**
 * The action card the conversation should be showing, or null.
 *
 * The NEWEST card wins, whatever its status. Two cards for different actions can
 * be open at once — a QBank block and a support message — and the conversation
 * used to keep a single pointer at "the last proposal that happened", which a
 * resolution for the older one could move; the student then answered a card that
 * was not the one they were looking at (ORA-750 review round 1, SF3).
 *
 * Status is deliberately NOT part of the choice (round 2, SF2). Preferring a
 * pending card meant that confirming the newest one swapped the older, still
 * pending card in over the top of the receipt — taking away the "Open" button
 * for the block that had just been built. A card the student answers stays until
 * they put it away with Done, or until the next turn retires it; only then does
 * the one behind it come forward.
 */
export const selectPendingActionProposal = (
  state: ChatRootState,
  conversationId: string | null
): ChatActionProposal | null => {
  const conversation = selectConversation(state, conversationId);
  if (!conversation) return null;

  // Reverse order: `turnIds` is append-only, so the last match is the newest.
  for (let index = conversation.turnIds.length - 1; index >= 0; index -= 1) {
    const proposal =
      state.chat.turns[conversation.turnIds[index]]?.actionProposal;

    if (proposal) return proposal;
  }

  return null;
};

/**
 * Follow-ups from the conversation's most recent turn, which is where the UI
 * shows them (ORA-606).
 *
 * Only what the model actually suggested: a turn that finished without any
 * offers none. There used to be a deterministic pair behind them, and a chip
 * Ora did not choose to offer is a suggestion about nothing — it fires a turn
 * whose only context is "this", so it was removed rather than kept as a
 * floor. A turn still streaming has not earned chips yet, and one that errored
 * or was stopped should offer a retry rather than a next question.
 *
 * A conversation read back from history has turns again (ORA-604): the reducer
 * rebuilds one per answer the server persisted a trail or chips for, so a
 * reopened thread offers the same next questions it offered live.
 */
export const selectLatestFollowups = (
  state: ChatRootState,
  conversationId: string | null
): ChatFollowup[] => {
  const turnIds = selectConversation(state, conversationId)?.turnIds ?? [];
  const latestTurnId = turnIds[turnIds.length - 1];
  const turn = latestTurnId ? state.chat.turns[latestTurnId] : undefined;

  if (!turn || turn.status !== 'done') return NO_FOLLOWUPS;

  return turn.followups.length > 0 ? turn.followups : NO_FOLLOWUPS;
};

/**
 * Materialized message list. Memoized because it builds a new array; prefer
 * `selectConversationMessageIds` + `selectMessage` in list components so a
 * single streaming message does not re-render the whole thread.
 */
export const selectConversationMessages = createSelector(
  [(state: ChatRootState) => state.chat.messages, selectConversationMessageIds],
  (messages, messageIds): ChatMessage[] =>
    messageIds.flatMap((id) => (messages[id] ? [messages[id]] : []))
);

export const selectMessageBlocks = createSelector(
  [
    (state: ChatRootState) => state.chat.blocks,
    (state: ChatRootState, messageId: string) =>
      state.chat.messages[messageId]?.blockIds ?? [],
  ],
  (blocks, blockIds): ChatBlock[] =>
    blockIds.flatMap((id) => (blocks[id] ? [blocks[id]] : []))
);

/**
 * Whether the conversation already shows the server's `set_result` row for this
 * set.
 *
 * That row is written by the return from a *launched* session
 * (`POST /study-sets/:id/return`), and it says the same thing the line a folded
 * set leaves behind does — so a set with a row must not offer the fold, or the
 * thread would carry the summary twice (ORA-833). A set finished in the thread
 * has no row, however completed the payload says it is: the in-thread write
 * path only marks the items done.
 *
 * Any row counts, including the "Paused at 2 of 5" one a mid-session return
 * writes: such a set keeps the label it had before ORA-833 even if the student
 * finishes it in the thread afterwards. Reading the row's own status to allow
 * that would be a second rule for a case the thread has never shown.
 *
 * Conversation-scoped and not memoized: it answers a boolean, and the row
 * arrives in a message of its own, so a per-message selector cannot see it.
 */
export const selectHasSetResultRow = (
  state: ChatRootState,
  conversationId: string,
  setId: string
): boolean =>
  Object.values(state.chat.blocks).some(
    (block) =>
      block.kind === 'set_result' &&
      block.conversationId === conversationId &&
      block.payload.setId === setId
  );

/**
 * Block kinds a server-written bookkeeping row carries: the `set_result` line a
 * return writes (ORA-681), and the study set "Start a full session" links or
 * "Study Now" mints from a missed question (ORA-678/831).
 *
 * The kinds alone do not make a row one — a turn can serve a study set too —
 * which is what the other conditions of {@link isBookkeepingRow} are for.
 *
 * `question` and `flashcards` are deliberately absent, even though an add-block
 * badge writes rows of exactly that shape (ORA-546): a turn can answer with one
 * served question and no words at all, and once the thread is re-read the two
 * are indistinguishable from here — the read does not say which rows the server
 * wrote for itself. Those rows keep the footer they have always had.
 */
const BOOKKEEPING_BLOCK_KINDS: ReadonlySet<string> = new Set([
  'set_result',
  'study_set',
]);

/**
 * A row the server wrote for its own bookkeeping rather than an answer to
 * anything: no words, no turn behind it, and nothing in it but content some
 * control asked to be put in the thread.
 *
 * It is not an answer, so it must not be re-run, superseded or rated: there is
 * no question it replies to, and naming it in a regenerate would replace the
 * bookkeeping instead of the answer the student is looking at.
 *
 * "No turn" is what separates it from a turn that answered by serving a set and
 * said nothing in words: that turn records what it did, live and restored, and a
 * bookkeeping row records nothing.
 */
const isBookkeepingRow = (
  state: ChatRootState,
  message: ChatMessage
): boolean =>
  message.role === 'assistant' &&
  message.text === '' &&
  message.turnId === null &&
  message.blockIds.length > 0 &&
  message.blockIds.every((blockId) => {
    const kind = state.chat.blocks[blockId]?.kind;
    return kind !== undefined && BOOKKEEPING_BLOCK_KINDS.has(kind);
  });

/** Whether this row is the server's bookkeeping rather than an answer. */
export const selectIsBookkeepingRow = (
  state: ChatRootState,
  messageId: string
): boolean => {
  const message = state.chat.messages[messageId];
  return message ? isBookkeepingRow(state, message) : false;
};

/**
 * Is this the answer at the bottom of the thread?
 *
 * Regenerate is offered on the latest answer alone (ORA-459). Re-running an
 * answer from the middle of a conversation would append the replacement at the
 * bottom, so the thread the student read back would have the same question
 * answered in two different places — and the runtime would supersede a message
 * that is no longer the one the thread ends on.
 *
 * A bookkeeping row is skipped: it is not an answer, so a "Study Now" that puts
 * one at the bottom must not take the chips and the re-run off the answer above
 * it (ORA-832).
 */
export const selectIsLatestAssistantMessage = (
  state: ChatRootState,
  messageId: string
): boolean => {
  const message = state.chat.messages[messageId];
  if (!message || message.role !== 'assistant') return false;

  const messageIds = selectConversationMessageIds(
    state,
    message.conversationId
  );

  for (let i = messageIds.length - 1; i >= 0; i -= 1) {
    const candidate = state.chat.messages[messageIds[i]];

    if (candidate?.role === 'assistant' && !isBookkeepingRow(state, candidate))
      return candidate.id === messageId;
  }

  return false;
};

/**
 * The server id a re-run of this conversation's last answer must name.
 *
 * `regenerateOf` addresses a row in the database, so it is only ever a server
 * message id — a client-side uuid names nothing and supersedes nothing. It is
 * also the LATEST persisted answer rather than the one a bubble happens to
 * remember: after a regenerate the first answer has already been superseded,
 * and naming it again would leave the second one standing (ORA-459).
 *
 * Null when the thread holds no persisted answer at all — a first turn that
 * died before it wrote anything. The re-run then goes out as a fresh ask.
 */
export const selectRegenerateTargetId = (
  state: ChatRootState,
  conversationId: string | null
): string | null => {
  const messageIds = selectConversationMessageIds(state, conversationId);

  for (let i = messageIds.length - 1; i >= 0; i -= 1) {
    const candidate = state.chat.messages[messageIds[i]];

    if (
      candidate?.role === 'assistant' &&
      candidate.serverId &&
      // A bookkeeping row has a real id and is not an answer: re-running it
      // would supersede the row instead of the answer above it (ORA-831).
      !isBookkeepingRow(state, candidate)
    ) {
      return candidate.serverId;
    }
  }

  return null;
};

/**
 * The envelope a turn was sent with, found from the assistant message a retry
 * or regenerate names. `regenerateOf` can be either the client id the slice
 * keys messages by or the server id `turn.done` reported, so both are tried.
 */
export const selectEnvelopeForRegenerate = (
  state: ChatRootState,
  messageId: string
): Record<string, unknown> | null => {
  const message =
    state.chat.messages[messageId] ??
    Object.values(state.chat.messages).find(
      (candidate) => candidate.serverId === messageId
    );

  const turnId = message?.turnId;

  return (turnId && state.chat.turns[turnId]?.envelope) || null;
};
